import { useState, useEffect, useRef } from 'react';
import { Spin, Alert, Button, Tabs } from 'antd';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useHelpStore } from '@/stores/helpStore';
import { getTopicById } from '@/constants/helpContent';
import { loadMarkdown, MarkdownLoadError } from '@/utils/loadMarkdown';
import { HelpContent } from './HelpContent';
import { HelpSearch } from './HelpSearch';
import { withDevModalFilePath } from '../common';

const { Modal, Drawer } = withDevModalFilePath('client/src/components/help/HelpModal.tsx');
const HELP_HISTORY_STATE_KEY = '__helpModalBlock';

/**
 * ヘルプモーダルコンポーネント
 *
 * 機能:
 * - デスクトップ: Modal表示
 * - モバイル: Drawer（下から表示）
 * - 検索機能
 * - Markdown読み込みとエラーハンドリング
 */
export function HelpModal() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isModalOpen, currentTopicId, closeModal, setCurrentTopic } = useHelpStore();
  const pushedHistoryRef = useRef(false);

  const [content, setContent] = useState('');
  const [error, setError] = useState<MarkdownLoadError | null>(null);
  const [loading, setLoading] = useState(false);

  // 現在のトピック情報を取得
  const currentTopic = currentTopicId ? getTopicById(currentTopicId) : undefined;

  // markdownPathからbaseImagePathを計算
  // 例: '/help/maintenance/calendar.md' -> '/help/maintenance'
  const baseImagePath = currentTopic?.markdownPath
    ? currentTopic.markdownPath.substring(0, currentTopic.markdownPath.lastIndexOf('/'))
    : '/help';

  // トピック変更時にMarkdownを読み込む
  useEffect(() => {
    if (!currentTopicId) {
      setContent('# ヘルプ\n\n左側のカテゴリから記事を選択してください。');
      return;
    }

    const topic = currentTopic;
    if (!topic) {
      setError(new MarkdownLoadError('Topic not found', currentTopicId));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    loadMarkdown(topic.markdownPath)
      .then(setContent)
      .catch((err) => {
        if (err instanceof MarkdownLoadError) {
          setError(err);
        } else {
          setError(new MarkdownLoadError('Unknown error', topic.markdownPath));
        }
      })
      .finally(() => setLoading(false));
  }, [currentTopicId, currentTopic]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const currentState = window.history.state;
    const baseState =
      typeof currentState === 'object' && currentState !== null ? currentState : {};
    const hasMarker = Boolean(
      (baseState as Record<string, unknown>)[HELP_HISTORY_STATE_KEY]
    );

    if (!hasMarker) {
      window.history.pushState(
        { ...baseState, [HELP_HISTORY_STATE_KEY]: true },
        '',
        window.location.href
      );
      pushedHistoryRef.current = true;
    } else {
      pushedHistoryRef.current = false;
    }

    const handlePopState = () => {
      const nextState = window.history.state;
      const nextBase =
        typeof nextState === 'object' && nextState !== null ? nextState : {};
      window.history.pushState(
        { ...nextBase, [HELP_HISTORY_STATE_KEY]: true },
        '',
        window.location.href
      );
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (pushedHistoryRef.current) {
        const latestState = window.history.state;
        const hasLatestMarker =
          typeof latestState === 'object' &&
          latestState !== null &&
          (latestState as Record<string, unknown>)[HELP_HISTORY_STATE_KEY] === true;
        if (hasLatestMarker) {
          window.history.back();
        }
        pushedHistoryRef.current = false;
      }
    };
  }, [isModalOpen]);

  const handleSelectTopic = (topicId: string) => {
    setCurrentTopic(topicId);
  };

  // コンテンツ表示
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          type="error"
          message="読み込みエラー"
          description={error.message}
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              再読み込み
            </Button>
          }
        />
      );
    }

    return <HelpContent content={content} baseImagePath={baseImagePath} currentTopicId={currentTopicId || undefined} />;
  };

  // モバイル表示
  if (isMobile) {
    return (
      <Drawer
        open={isModalOpen}
        onClose={closeModal}
        placement="bottom"
        height="90vh"
        styles={{
          body: {
            padding: '12px',
            overflow: 'auto',
          },
          header: {
            padding: '12px 16px',
          },
        }}
        title="ヘルプ"
      >
        <Tabs
          defaultActiveKey="content"
          items={[
            {
              key: 'content',
              label: 'ヘルプ',
              children: (
                <div style={{ padding: '0 8px' }}>{renderContent()}</div>
              ),
            },
            {
              key: 'search',
              label: '検索',
              children: (
                <div style={{ padding: '0 8px' }}>
                  <HelpSearch
                    onSelectTopic={handleSelectTopic}
                    currentTopicId={currentTopicId || undefined}
                  />
                </div>
              ),
            },
          ]}
        />
      </Drawer>
    );
  }

  // デスクトップ表示
  return (
    <Modal
      open={isModalOpen}
      onCancel={closeModal}
      width={900}
      footer={null}
      styles={{
        body: {
          padding: '24px',
          height: '70vh',
          overflow: 'hidden',
        },
      }}
      title="ヘルプ"
    >
      <div style={{ display: 'flex', height: '100%', gap: '24px' }}>
        {/* 左側: 検索 */}
        <div style={{ width: '300px', borderRight: '1px solid #f0f0f0', paddingRight: '16px', overflow: 'auto' }}>
          <HelpSearch
            onSelectTopic={handleSelectTopic}
            currentTopicId={currentTopicId || undefined}
          />
        </div>

        {/* 右側: コンテンツ */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
}
